import {Injectable} from '@angular/core';
import {Column} from 'asax-common';
import {Observable, of} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {ExportFormat} from './export-container/export-container.component';
import {ExportColumnFormatterFactory} from './export-colum-formatter/factory.export.column.fomatter';

const exportColumnFormatterFactory = new ExportColumnFormatterFactory();

const fetchValueFromRow = (row: any, column: Column): any => {
  if (column.exportColumnFormatterType) {
    const formatter = exportColumnFormatterFactory.getFormatter(column.exportColumnFormatterType);

    if (formatter) {
      return formatter.format(row);
    }
  }

  const properties = column.prop.split('.');
  let value = row;

  try {
    for (const property of properties) {
      value = value[property];
    }
  } catch (e) {
  }

  return value;
};

interface TableExporter {
  export(): Observable<string>;
}

@Injectable({
  providedIn: 'root'
})
export class TableExportService {
  export(rows: any[], columns: Column[], exportFormat: ExportFormat): Observable<string> {
    const exporter = this.getExporter(rows, columns, exportFormat);
    return exporter.export();
  }

  private getExporter(rows: any[], columns: Column[], exportFormat: ExportFormat): TableExporter {
    switch (exportFormat) {
      case 'csv':
        return new DelimiterExporter(rows, columns, ',');
      case 'tsv':
        return new DelimiterExporter(rows, columns, '\t');
      case 'xml':
        return new XmlExporter(rows, columns);
    }
  }
}

class DelimiterExporter implements TableExporter {
  private readonly lines = [];

  private header: string;

  constructor(private readonly rows: any[], private readonly columns: Column[], private readonly delimiter: string) {
  }

  export(): Observable<string> {
    return of(this.rows).pipe(
      tap(() => this.buildHeader()),
      tap(() => this.buildLines()),
      map(() => this.buildExportedFile())
    );
  }

  buildHeader(): void {
    this.header = this.columns.map(column => column.name).join(this.delimiter);
  }

  buildLines(): void {
    for (const row of this.rows) {
      this.buildLine(row);
    }
  }

  buildLine(row: any): void {
    const values = [];

    for (const column of this.columns) {
      let value = fetchValueFromRow(row, column);

      if (typeof value === 'string' && value.includes(this.delimiter)) {
        value = `"${value}"`;
      }

      values.push(value);
    }

    this.lines.push(values.join(this.delimiter));
  }

  buildExportedFile(): string {
    return `${this.header}\n${this.lines.join('\n')}`;
  }
}

class XmlExporter implements TableExporter {
  private xml = '';

  constructor(private readonly rows: any[], private readonly columns: Column[]) {
  }

  export(): Observable<string> {
    return of(this.rows).pipe(
      tap(() => this.buildXml()),
      map(() => this.xml),
      tap(e => console.log(e))
    );
  }

  buildXml(): void {
    this.xml += '<?xml version=\'1.0\'?>\n';
    this.xml += '<VOTABLE xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns="http://www.ivoa.net/xml/VOTable/v1.3" version="1.3">\n';
    this.xml += '\t<RESOURCE type="results">\n';
    this.xml += '\t\t<INFO name="QUERY_STATUS" value="Ok"/>\n';
    this.xml += '\t\t<INFO name="PROVIDER" value="ALMA"/>\n';
    this.xml += '\t\t<TABLE>\n';

    this.buildColumns();

    this.xml += '\t\t\t<DATA>\n';
    this.xml += '\t\t\t\t<TABLEDATA>\n';

    this.buildRows();

    this.xml += '\t\t\t\t</TABLEDATA>\n';
    this.xml += '\t\t\t</DATA>\n';

    this.xml += '\t\t</TABLE>\n';
    this.xml += '\t</RESOURCE>\n';
    this.xml += '</VOTABLE>\n';
  }

  buildColumns(): void {
    for (const column of this.columns) {
      this.buildColumn(column);
    }
  }

  buildColumn(column): void {
    this.xml += '\t\t\t<FIELD name="' + (column.xmlName ? column.xmlName : column.name) + '"';

    if (column.dataType) {
      this.xml += ' datatype="' + column.dataType + '"';
    }
    if (column.uType) {
      this.xml += ' utype="' + column.uType + '"';
    }
    if (column.ucd) {
      this.xml += ' ucd="' + column.ucd + '"';
    }
    if (column.xType) {
      this.xml += ' xtype="' + column.xType + '"';
    }
    if (column.arraySize) {
      this.xml += ' arraysize="' + column.arraySize + '"';
    }

    this.xml += '/>\n';
  }

  buildRows(): void {
    for (const row of this.rows) {
      this.xml += '\t\t\t\t\t<TR>\n';
      this.buildRow(row);
      this.xml += '\t\t\t\t\t</TR>\n';
    }
  }

  buildRow(row: any): void {
    for (const column of this.columns) {
      const value = fetchValueFromRow(row, column);
      this.xml += '\t\t\t\t\t\t<TD>' + value + '</TD>\n';
    }
  }
}
